import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Send, Sparkles, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTranslate } from '../context/LanguageContext';

export default function HomeNewsletter() {
  const { language, t, dir } = useTranslate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage(t('يرجى إدخال عنوان بريد إلكتروني صالح.'));
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      // Simulate network request
      await new Promise((resolve) => setTimeout(resolve, 1200));

      const stored = localStorage.getItem('newsletter_subscribers');
      const subscribers: string[] = stored ? JSON.parse(stored) : [];

      if (subscribers.includes(email.trim().toLowerCase())) {
        setStatus('error');
        setMessage(t('هذا البريد الإلكتروني مشترك لدينا بالفعل!'));
        return;
      }

      subscribers.push(email.trim().toLowerCase());
      localStorage.setItem('newsletter_subscribers', JSON.stringify(subscribers));

      setStatus('success');
      setMessage(t('تم الاشتراك بنجاح! شكراً لانضمامك ونقدّر ثقتك بمحتوانا الفكري.'));
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(t('حدث خطأ أثناء الاشتراك. يرجى المحاولة مرة أخرى.'));
    }
  };

  return (
    <section className="relative py-20 px-4 md:px-8 bg-zinc-950 overflow-hidden" dir={dir} id="home-newsletter">
      {/* Elegantly Polished Background Gradients and Lines */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(220,180,58,0.03)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute left-[-10%] top-[20%] w-96 h-96 bg-zinc-900/40 rounded-full blur-[100px] pointer-events-none" />
      
      {/* Decorative Golden Ambient Accent Rings */}
      <div className="absolute -top-12 -right-12 w-64 h-64 border border-secondary/10 rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-80 h-80 border-2 border-secondary/5 rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative bg-zinc-950 border border-secondary/20 dark:border-secondary/20 rounded-[2.5rem] p-8 md:p-14 overflow-hidden group shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
        >
          {/* Subtle reflection overlay */}
          <div className="absolute inset-0 bg-gradient-to-tl from-secondary/[0.02] via-transparent to-transparent pointer-events-none" />
          
          {/* Inner ambient glow */}
          <div className="absolute right-0 top-0 w-80 h-80 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
          
          {/* Main Layout Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Description Column */}
            <div className={cn("lg:col-span-7 space-y-6", language === 'ar' ? "text-right" : "text-left")}>
              <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/30 text-secondary text-[11px] font-bold uppercase tracking-widest px-4 py-2 rounded-full backdrop-blur-md">
                <Sparkles size={12} className="animate-pulse" />
                <span>{t('النشرة الدورية للوعي والتنوير')}</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-black text-white leading-[1.15] italic tracking-tight">
                {language === 'ar' ? (
                  <>كن شريكاً في <span className="text-secondary font-black">الوعي والمعرفة</span></>
                ) : (
                  <>Be a Partner in <span className="text-secondary font-black">Awareness & Knowledge</span></>
                )}
              </h2>
              
              <p className="text-sm md:text-base text-zinc-400 font-medium leading-[1.8] max-w-2xl">
                {t('انضم لمجتمعنا الفكري والسياسي، واحصل على أحدث التحليلات المعمقة، والتقارير الحصرية، ومساهمات كبار الكتاب التي تسلط الضوء على واقع وسياسات بلادنا وتطلعات التنوير، مباشرة في صندوق بريدك الوارد.')}
              </p>
              
              <div className={cn("flex flex-wrap gap-x-6 gap-y-2 text-zinc-400 text-xs", language === 'ar' ? "justify-start" : "justify-start")}>
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-secondary" />
                  <span>{t('بدون أي رسائل مزعجة')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                  <span>{t('إلغاء الاشتراك بنقرة واحدة وفي أي وقت')}</span>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-5 w-full bg-zinc-900/40 p-6 md:p-8 rounded-[2rem] border border-white/5 backdrop-blur-sm self-stretch flex flex-col justify-center relative">
              <div className="absolute top-4 left-4 text-zinc-700 pointer-events-none hidden md:block">
                <Mail size={40} className="stroke-[1] opacity-20" />
              </div>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <div className="space-y-1">
                  <label htmlFor="newsletter-email" className={cn("block text-xs font-bold text-zinc-400 mr-1", language === 'ar' ? "text-right" : "text-left")}>
                    {t('عنوان بريدك الإلكتروني')}
                  </label>
                  <div className="relative">
                    <input
                      id="newsletter-email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (status === 'error') setStatus('idle');
                      }}
                      disabled={status === 'loading'}
                      className={cn(
                        "w-full h-14 pl-5 rounded-2xl bg-zinc-950/80 border border-zinc-800 text-sm font-bold text-white placeholder-zinc-600 transition-all outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/40 text-left antialiased",
                        language === 'ar' ? 'pr-12' : 'pr-5 pl-12'
                      )}
                      required
                    />
                    <div className={cn("absolute top-1/2 -translate-y-1/2 text-zinc-500", language === 'ar' ? "right-4" : "left-4")}>
                      <Mail size={18} />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={cn(
                    "w-full h-14 bg-secondary hover:bg-secondary/95 text-zinc-950 font-black text-sm rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-secondary/10 relative overflow-hidden group/btn active:scale-95 disabled:opacity-50 select-none cursor-pointer"
                  )}
                >
                  {status === 'loading' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
                      <span>{t('جاري إرسال طلب الاشتراك...')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('اشترك في القائمة البريدية')}</span>
                      <Send size={16} className="transition-transform duration-300 group-hover/btn:translate-x-[-4px] group-hover/btn:translate-y-[2px]" />
                    </>
                  )}
                </button>
              </form>

              <AnimatePresence mode="wait">
                {message && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "flex items-start gap-3 p-4 rounded-xl text-xs font-bold mt-4 leading-relaxed",
                      status === 'success' 
                        ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" 
                        : "bg-rose-500/10 border border-rose-500/20 text-rose-400"
                    )}
                  >
                    <div className="shrink-0 mt-0.5">
                      {status === 'success' ? (
                        <CheckCircle2 size={16} className="text-emerald-400" />
                      ) : (
                        <AlertCircle size={16} className="text-rose-400" />
                      )}
                    </div>
                    <span className={language === 'ar' ? "text-right" : "text-left"}>{message}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );
}
